/**
 * GPU - Main Orchestrator
 *
 * Central manager for WebGPU rendering in the application.
 * Coordinates scene creation, route transitions, and the render loop.
 *
 * Architecture:
 * - GPU (this class): Manages scenes, context, transitions
 * - Scene: Per-route texture planes bound to DOM
 * - GPURenderer (from kido/gpu): WebGPU drawing
 *
 * Lifecycle:
 * 1. Constructor: Creates canvas reference
 * 2. load(): Initializes WebGPU via kido/gpu (async)
 * 3. intro(): Creates scene for initial route
 * 4. init(): Sets initial scene as active
 * 5. run(): Starts render loop and resize observers
 *
 * Route Transitions:
 * - prepareTransition(): Sets up crossfade between old/new routes
 * - commitTransition(): Finalizes transition, sets new scene as active
 *
 * Integration Points:
 * - App.gpu: Stored in app context for global access
 * - loader: Populates media store with textures
 * - transition.ts: Calls prepareTransition/commitTransition
 * - page-manager: Calls scene methods via App.gpu
 *
 * Post-Processing:
 * - Enable via: App.gpu.postFx.enabled = true
 * - Configure via: App.gpu.postFx.amount, .frequency, .radius, .speed
 */

import {
  GPURenderer,
  isWebGPUSupported,
  PostPass,
  PostProcessor,
  type GPUPlane,
  type GPUScene,
} from "kido/gpu";
import { Raf, getFrameRatio } from "kido/raf";
import { ResizeHub } from "kido/resize";

import { App, type RouteMediaStore } from "@app/context";

import distortionShader from "../../../../../packages/kido/src/gpu/post/shaders/distortion.wgsl?raw";


import { gpuTimings, getGpuHud } from "./hud";
import Scene, { type ScenePlane } from "./scene";
import lensBlurShader from "./shaders/lens-blur.wgsl?raw";

// ==================== Nudge Constants ====================
const NUDGE_MAX_OFFSET = 12;
const NUDGE_LERP_SPEED = 0.1;
const NUDGE_SCROLL_DECAY = 0.88;
const NUDGE_SCROLL_SCALE = 0.04;
const NUDGE_SCROLL_CUTOFF = 0.1;
const NUDGE_MOUSE_RADIUS = 250;
const NUDGE_MOUSE_STRENGTH = 15;
const NUDGE_STAGGER_SCALE = 0.00012;
const NUDGE_TILT_MAX = 0.06; // ~3.4° max tilt
const NUDGE_TILT_MOUSE = 0.04; // mouse-driven tilt strength
const NUDGE_TILT_SCROLL = 0.03; // scroll-driven tilt strength

/**
 * Post-processing effect configuration.
 * Modify these values to calibrate the effect.
 */
export interface PostFxConfig {
  /** Enable/disable post-processing */
  enabled: boolean;
  /** Distortion strength (0.0 to 0.2, default 0.05) */
  amount: number;
  /** Ripple frequency (5.0 to 30.0, default 15.0) */
  frequency: number;
  /** Effect radius around mouse (0.1 to 1.0, default 0.3) */
  radius: number;
  /** Animation speed (0.5 to 5.0, default 2.0) */
  speed: number;
}

/**
 * State for a route transition (crossfade)
 */
interface TransitionState {
  /** Outgoing scene (current route) */
  fromScene: Scene | null;
  /** Incoming scene (new route) */
  toScene: Scene | null;
  /** Transition progress: 0 = fully from, 1 = fully to */
  progress: number;
}

/**
 * Configuration for throttled bounds updates
 */
interface BoundsUpdateConfig {
  mode: "always" | "throttled";
  interval: number;
  last: number;
  force: boolean;
}

/**
 * Interface for renderer-like object (for compatibility)
 */
interface RendererLike {
  needsRedraw: boolean;
  extraFrames: number;
  boundsFrozen: boolean;
  boundsUpdate: BoundsUpdateConfig;
  resize: () => void;
  requestBoundsUpdate: () => void;
  requestRedraw?: () => void;
  setBoundsUpdateMode?: (mode: "always" | "throttled") => void;
}

/**
 * GPU class - Main WebGPU orchestrator.
 *
 * @example
 * const gpu = new GPU();
 * await gpu.load();
 *
 * // After textures are loaded:
 * gpu.intro();
 * gpu.init();
 * gpu.run();
 *
 * // Enable post-processing:
 * gpu.postFx.enabled = true;
 *
 * // Calibrate effect:
 * gpu.postFx.amount = 0.08;    // More distortion
 * gpu.postFx.frequency = 20;   // More ripples
 * gpu.postFx.radius = 0.4;     // Larger area
 * gpu.postFx.speed = 3;        // Faster animation
 *
 * // During route change:
 * gpu.prepareTransition("/", "/about");
 * // ... animate transition.progress from 0 to 1 ...
 * gpu.commitTransition();
 */
export default class GPU {
  // ==================== Scene Management ====================

  /**
   * Cache of scenes by route URL.
   * Scenes are created on-demand and reused.
   */
  readonly scenes: Record<string, Scene>;

  /**
   * Media store populated by loader.
   * Structure: { [url]: { main: RouteMediaSlot[] } }
   */
  media: RouteMediaStore;

  /**
   * Currently active scene (being rendered).
   */
  scene: Scene | null;

  /**
   * Current transition state (null when not transitioning).
   */
  transition: TransitionState | null;

  /**
   * Flag indicating a mutation (route change) is in progress.
   */
  isMutating: boolean;

  /**
   * When true, a page owns the render loop.
   * The main _frame() will skip rendering but still update bounds.
   */
  pageOwnsRender: boolean;

  /**
   * When true, video textures will play. When false, they pause after first frame.
   * Toggle at runtime: App.gpu.videosPlaying = true / false
   */
  videosPlaying: boolean;

  // ==================== WebGPU Context ====================

  /**
   * Canvas element for WebGPU rendering.
   */
  readonly canvas: HTMLCanvasElement | null;

  /**
   * WebGPU device (from kido/gpu).
   */
  device: GPUDevice | null;

  /**
   * GPURenderer from kido/gpu.
   */
  private gpuRenderer: GPURenderer | null;

  /**
   * Renderer interface for compatibility with existing code.
   */
  renderer: RendererLike | null;

  // ==================== Render State ====================

  /**
   * Flag to request a redraw on next frame.
   */
  private needsRedraw: boolean;

  /**
   * Number of extra frames to render after changes settle.
   */
  private extraFrames: number;

  /**
   * When true, skip DOM bounds measurements.
   */
  boundsFrozen: boolean;

  /**
   * Configuration for throttled bounds updates.
   */
  private boundsUpdate: BoundsUpdateConfig;

  // ==================== Utilities ====================

  /**
   * Helper to request extra render frames.
   */
  requestExtraFrames: () => void;

  /**
   * Resize observer ID for cleanup.
   */
  private _roId: symbol | null;

  /**
   * RAF instance for render loop.
   */
  private _raf: Raf | null;

  /**
   * Accumulated easing time for current frame (for HUD).
   */
  private _easingTime: number;

  // ==================== Post-Processing ====================

  /**
   * Post-processing configuration.
   * Toggle and calibrate effects via this object.
   */
  readonly postFx: PostFxConfig;

  /** Post-processor instance */
  private _postProcessor: PostProcessor | null;

  /** Distortion pass */
  private _distortPass: PostPass | null;

  /** Lens blur pass */
  private _lensPass: PostPass | null;

  /** Offscreen texture for scene rendering */
  private _sceneTexture: GPUTexture | null;

  /** View of the scene texture */
  private _sceneTextureView: GPUTextureView | null;

  /** Mouse X position (0-1) */
  private _mouseX: number;

  /** Mouse Y position (0-1) */
  private _mouseY: number;

  /** Raw mouse position in viewport pixels */
  private _mousePixelX: number;
  private _mousePixelY: number;

  /** Per-plane hover tilt easing state */
  private _hoverTilts = new WeakMap<
    ScenePlane,
    { tX: number; tY: number; cX: number; cY: number }
  >();

  /** Animation time (seconds) */
  private _time: number;

  /** Previous frame timestamp for delta calculation */
  private _prevFrameTime: number;

  /** Whether post-processing is initialized */
  private _postFxReady: boolean;

  /** Bound scroll handler for native scroll sync */
  private _onScroll: (() => void) | null;

  /** Pre-render callback, runs before bounds update and render pass */
  private _preRenderCb: (() => void) | null;

  // ==================== Oscillation State ====================

  /** Per-plane nudge state */
  private _oscState: WeakMap<ScenePlane, { offsetY: number; tiltX: number; tiltY: number }>;

  /** Accumulated scroll velocity for oscillation */
  private _oscScrollVel: number;

  /** Previous native scrollY for delta tracking */
  private _oscPrevScrollY: number;

  /** Bound wheel listener reference for cleanup */
  private _oscWheelListener: ((e: WheelEvent) => void) | null;

  /**
   * Creates GPU orchestrator.
   */
  constructor() {
    // Initialize scene management
    this.scenes = {};
    this.media = {};
    this.scene = null;
    this.transition = null;
    this.isMutating = false;
    this.pageOwnsRender = false;
    // this.videosPlaying = !import.meta.env.DEV;
    this.videosPlaying = true;

    // Get canvas element
    this.canvas = document.getElementById("canvas") as HTMLCanvasElement | null;

    // WebGPU context (initialized in load())
    this.device = null;
    this.gpuRenderer = null;

    // Render state
    this.needsRedraw = true;
    this.extraFrames = 0;
    this.boundsFrozen = false;
    this.boundsUpdate = {
      mode: "always",
      interval: 0,
      last: 0,
      force: false,
    };

    // Create renderer interface for compatibility
    this.renderer = {
      boundsFrozen: false,
      boundsUpdate: this.boundsUpdate,
      resize: () => this.resize(),
      requestBoundsUpdate: () => this.requestBoundsUpdate(),
      requestRedraw: () => this.requestRedraw(),
      setBoundsUpdateMode: (mode: "always" | "throttled") =>
        this.setBoundsUpdateMode(mode),
      needsRedraw: false,
      extraFrames: 0,
    };

    // Define getters/setters that sync with GPU class state
    Object.defineProperty(this.renderer, "needsRedraw", {
      get: () => this.needsRedraw,
      set: (value: boolean) => {
        this.needsRedraw = value;
      },
    });
    Object.defineProperty(this.renderer, "extraFrames", {
      get: () => this.extraFrames,
      set: (value: number) => {
        this.extraFrames = value;
      },
    });

    // Bind helper methods
    this.requestExtraFrames = () => {
      this.needsRedraw = true;
      this.extraFrames = 2;
      if (this.gpuRenderer) {
        this.gpuRenderer.needsRedraw = true;
        this.gpuRenderer.extraFrames = 2;
      }
    };

    // Resize observer ID
    this._roId = null;
    this._raf = null;
    this._easingTime = 0;

    // ==================== Post-Processing Setup ====================

    // Default configuration - CHANGE THESE TO CALIBRATE
    this.postFx = {
      enabled: false, // Post-processing disabled
      amount: 0.05, // Distortion strength
      frequency: 15.0, // Ripple frequency
      radius: 0.3, // Effect radius
      speed: 2.0, // Animation speed
    };

    this._postProcessor = null;
    this._distortPass = null;
    this._lensPass = null;
    this._sceneTexture = null;
    this._sceneTextureView = null;
    this._mouseX = 0.5;
    this._mouseY = 0.5;
    this._mousePixelX = -9999;
    this._mousePixelY = -9999;
    this._time = 0;
    this._prevFrameTime = 0;
    this._postFxReady = false;
    this._onScroll = null;
    this._preRenderCb = null;

    // Oscillation state
    this._oscState = new WeakMap();
    this._oscScrollVel = 0;
    this._oscPrevScrollY = window.scrollY;
    this._oscWheelListener = null;

    // Set up mouse tracking
    this._setupMouse();

    // Set up oscillation input
    this._setupOscillation();
  }

  /**
   * Sets up mouse tracking for post-processing effects.
   */
  private _setupMouse(): void {
    document.addEventListener("mousemove", (e) => {
      if (!this.canvas) return;
      const rect = this.canvas.getBoundingClientRect();
      this._mouseX = (e.clientX - rect.left) / rect.width;
      this._mouseY = (e.clientY - rect.top) / rect.height;
      this._mousePixelX = e.clientX;
      this._mousePixelY = e.clientY;
      // Request redraw when mouse moves (for oscillation + post-fx)
      this.needsRedraw = true;
    });

    document.addEventListener("mouseleave", () => {
      this._mouseX = 0.5;
      this._mouseY = 0.5;
      this._mousePixelX = -9999;
      this._mousePixelY = -9999;
    });
  }

  /**
   * Sets up oscillation input (wheel listener).
   */
  private _setupOscillation(): void {
    this._oscWheelListener = (e: WheelEvent) => {
      this._oscScrollVel += e.deltaY;
    };
    document.addEventListener("wheel", this._oscWheelListener, {
      capture: true,
      passive: true,
    });
  }

  /**
   * Initializes WebGPU via kido/gpu.
   */
  async load(): Promise<void> {
    if (!this.canvas) {
      console.error("[GPU] No canvas element found");
      return;
    }

    if (!isWebGPUSupported()) {
      console.warn("[GPU] WebGPU not supported");
      return;
    }

    this.gpuRenderer = new GPURenderer({ canvas: this.canvas });

    const success = await this.gpuRenderer.init(this.canvas);
    if (!success) {
      console.error("[GPU] Failed to initialize GPURenderer");
      this.gpuRenderer = null;
      return;
    }

    this.device = this.gpuRenderer.getDevice();

    console.debug("[GPU] WebGPU initialized via kido/gpu");
  }

  /**
   * Initializes post-processing pipeline.
   * Called automatically when postFx.enabled is true and resources are ready.
   */
  private _initPostFx(): void {
    if (this._postFxReady || !this.device || !this.gpuRenderer) return;

    const format = this.gpuRenderer.getFormat();
    const { width, height } = this.gpuRenderer.getCanvasSize();

    if (!format || width === 0 || height === 0) return;

    // Create offscreen texture
    this._sceneTexture = this.device.createTexture({
      label: "Scene Texture",
      size: [width, height],
      format,
      usage:
        GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this._sceneTextureView = this._sceneTexture.createView();

    // Create post-processor
    this._postProcessor = new PostProcessor({
      device: this.device,
      format,
      width,
      height,
    });

    // Add distortion pass (disabled — keep for later use)
    this._distortPass = this._postProcessor.addPass({
      name: "distortion",
      shaderSource: distortionShader,
      uniformBufferSize: 48,
      enabled: false,
    });

    // Add lens blur pass
    this._lensPass = this._postProcessor.addPass({
      name: "lens-blur",
      shaderSource: lensBlurShader,
      uniformBufferSize: 32,
      enabled: true,
    });

    this._postFxReady = true;
    console.debug("[GPU] Post-processing initialized");
  }

  /**
   * Resizes post-processing textures.
   */
  private _resizePostFx(): void {
    if (!this._postFxReady || !this.device || !this.gpuRenderer) return;

    const format = this.gpuRenderer.getFormat();
    const { width, height } = this.gpuRenderer.getCanvasSize();

    if (!format || width === 0 || height === 0) return;

    this._sceneTexture?.destroy();

    this._sceneTexture = this.device.createTexture({
      label: "Scene Texture",
      size: [width, height],
      format,
      usage:
        GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this._sceneTextureView = this._sceneTexture.createView();

    this._postProcessor?.resize(width, height);
  }

  /**
   * Resolves a raw URL to a route key for scene caching.
   */
  private resolveRouteKey(urlRaw: string | null): string | null {
    if (!urlRaw) return null;

    const pageKey = App.config.routes[urlRaw];

    if (pageKey === "home" || (pageKey && pageKey.startsWith("case-"))) {
      return "/";
    }

    // Project pages share the /projects scene
    if (pageKey === "project") {
      return "/projects";
    }

    // Play detail pages share the /play scene
    if (pageKey === "play-detail") {
      return "/play";
    }

    return urlRaw;
  }

  /**
   * Ensures a scene exists for a route, creating if needed.
   */
  private ensureScene(
    url: string | null,
    pageKey: string | null
  ): Scene | null {
    if (!url) return null;

    if (!this.scenes[url]) {
      this.scenes[url] = new Scene(url, this, pageKey);
    }

    return this.scenes[url];
  }

  /**
   * Pre-creates scene for the initial route.
   */
  intro(): void {
    const url = this.resolveRouteKey(App.route.new.url);
    const pageKey = url ? App.config.routes[App.route.new.url ?? url] : null;

    if (!url) return;

    this.ensureScene(url, pageKey ?? null);
  }

  /**
   * Sets the initial scene as active.
   */
  init(): void {
    const urlRaw = App.route.new.url;
    const url = this.resolveRouteKey(urlRaw);
    const pageKey = urlRaw ? App.config.routes[urlRaw] : null;

    if (!url) return;

    const scene = this.ensureScene(url, pageKey ?? null);
    this.scene = scene;
  }

  /**
   * Starts the render loop and installs resize observers.
   */
  run(): void {
    if (!this.gpuRenderer) return;

    const resize = () => {
      App.win.w = window.innerWidth;
      App.win.h = window.innerHeight;

      this.resize();
      this.needsRedraw = true;
      if (this.renderer) this.renderer.needsRedraw = true;
      if (this.gpuRenderer) this.gpuRenderer.needsRedraw = true;

      try {
        App.sections?.resize?.();
      } catch {
        // Ignore errors
      }

      if (!this.boundsFrozen) {
        if (this.scene?.updateBounds) {
          this.scene.updateBounds();
        }
        if (this.transition) {
          this.transition.fromScene?.updateBounds?.();
          this.transition.toScene?.updateBounds?.();
        }
      }
    };

    this._roId = ResizeHub.add(resize);
    resize();

    // Sync plane bounds on native scroll to avoid 1-frame lag
    this._onScroll = () => {
      if (this.boundsFrozen) return;

      // Track native scroll delta for oscillation
      const sy = window.scrollY;
      const delta = sy - this._oscPrevScrollY;
      this._oscPrevScrollY = sy;
      if (Math.abs(delta) > 0.5) {
        this._oscScrollVel += delta;
      }

      this.requestBoundsUpdate();
      this.requestRedraw();
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });

    if (!this._raf) {
      this._raf = new Raf("renderer", () => this._frame());
    }

    this._raf.run();
  }

  /**
   * Internal frame callback.
   */
  private _frame(): void {
    if (!this.gpuRenderer) return;

    // Run pre-render callback before anything else so plane bounds
    // are current when the render pass reads them.
    this._preRenderCb?.();

    // Decay scroll velocity (frame-rate independent)
    const decayFactor = Math.pow(NUDGE_SCROLL_DECAY, getFrameRatio());
    this._oscScrollVel *= decayFactor;
    if (Math.abs(this._oscScrollVel) < NUDGE_SCROLL_CUTOFF) this._oscScrollVel = 0;
    else this.needsRedraw = true;

    if (!this.needsRedraw && this.extraFrames === 0) return;

    const hudVisible = getGpuHud()?.visible ?? false;
    const frameStart = hudVisible ? performance.now() : 0;

    this.needsRedraw = false;
    this.extraFrames = Math.max(0, this.extraFrames - 1);
    if (this.renderer) {
      this.renderer.needsRedraw = false;
      this.renderer.extraFrames = Math.max(0, this.renderer.extraFrames - 1);
    }

    if (hudVisible) {
      const boundsStart = performance.now();
      this._maybeUpdateBounds();
      gpuTimings.bounds = performance.now() - boundsStart;
    } else {
      this._maybeUpdateBounds();
    }

    // When a page owns the render loop, skip rendering here
    // The page's own RAF loop handles renderToTexture + post-processing
    if (this.pageOwnsRender) {
      if (hudVisible) {
        gpuTimings.render = 0;
        gpuTimings.easing = 0;
        gpuTimings.total = performance.now() - frameStart;
      }
      return;
    }

    this._easingTime = 0;

    const renderStart = hudVisible ? performance.now() : 0;

    if (
      this.transition &&
      this.transition.fromScene &&
      this.transition.toScene &&
      this.transition.fromScene !== this.transition.toScene
    ) {
      this._renderPair(
        this.transition.fromScene,
        this.transition.toScene,
        this.transition.progress ?? 0
      );
    } else {
      this._renderScene(this.scene);
    }

    if (hudVisible) {
      gpuTimings.render = performance.now() - renderStart;
      gpuTimings.easing = this._easingTime;
      gpuTimings.total = performance.now() - frameStart;
    }
  }

  /**
   * Conditionally updates scene bounds.
   */
  private _maybeUpdateBounds(): void {
    if (this.boundsFrozen) return;

    const now = performance.now();
    const cfg = this.boundsUpdate;
    const should =
      cfg.mode === "always" || cfg.force || now - cfg.last >= cfg.interval;

    if (should) {
      try {
        this.scene?.updateBounds?.();
        if (this.transition) {
          this.transition.fromScene?.updateBounds?.();
          this.transition.toScene?.updateBounds?.();
        }
      } catch {
        // Ignore
      }
      cfg.last = now;
      cfg.force = false;
    }
  }

  /**
   * Converts a ScenePlane to GPUPlane format and applies easing.
   */
  private _convertPlane(plane: ScenePlane, alpha: number): GPUPlane {
    const hudVisible = getGpuHud()?.visible ?? false;
    const easingStart = hudVisible ? performance.now() : 0;

    const op =
      typeof plane.opacityEased === "number"
        ? plane.opacityEased
        : (plane.opacity ?? 1);

    // Scale lerp factors by frame ratio for frame-rate independence
    const fr = getFrameRatio();

    const targetS = plane.strength ?? 0;
    const prevS = plane.strengthEased ?? 0;
    const alphaEase = 1 - Math.pow(1 - 0.09, fr);
    const easedS = prevS + (targetS - prevS) * alphaEase;
    plane.strengthEased = easedS;

    const targetTilt = plane.hoverTilt ?? 0;
    const prevTilt = plane.hoverTiltEased ?? 0;
    const tiltEase = 1 - Math.pow(1 - 0.12, fr);
    const easedTilt = prevTilt + (targetTilt - prevTilt) * tiltEase;
    plane.hoverTiltEased = easedTilt;

    const targetZoom = plane.hoverZoom ?? 0;
    const prevZoom = plane.hoverZoomEased ?? 0;
    const zoomEase = 1 - Math.pow(1 - 0.12, fr);
    const easedZoom = prevZoom + (targetZoom - prevZoom) * zoomEase;
    plane.hoverZoomEased = easedZoom;

    if (
      Math.abs(easedS - targetS) > 0.0005 ||
      Math.abs(easedTilt - targetTilt) > 0.0005 ||
      Math.abs(easedZoom - targetZoom) > 0.0005
    ) {
      this.needsRedraw = true;
      this.extraFrames = Math.max(this.extraFrames, 1);
      if (this.gpuRenderer) {
        this.gpuRenderer.needsRedraw = true;
        this.gpuRenderer.extraFrames = Math.max(
          this.gpuRenderer.extraFrames,
          1
        );
      }
    }

    if (hudVisible) {
      this._easingTime += performance.now() - easingStart;
    }

    const result: GPUPlane = {
      texture: plane.texture ?? null,
      sampler: plane.sampler ?? null,
      aspectRatio: plane.aspectRatio || 1,
      opacity: alpha * op,
      reveal: 1,
      bounds: plane.boundsPx
        ? {
          x: plane.boundsPx.x,
          y: plane.boundsPx.y,
          w: plane.boundsPx.w,
          h: plane.boundsPx.h,
          z: plane.boundsPx.z,
        }
        : null,
    };
    if (plane.id) {
      result.id = plane.id;
    }
    if (plane.borderRadius !== undefined) {
      result.borderRadius = plane.borderRadius;
    }
    if (plane.shadowBlur !== undefined) {
      result.shadowBlur = plane.shadowBlur;
    }
    if (plane.shadowOffset !== undefined) {
      result.shadowOffset = plane.shadowOffset;
    }
    if (plane.shadowColor !== undefined) {
      result.shadowColor = plane.shadowColor;
    }
    if (plane.rotation !== undefined) {
      result.rotation = plane.rotation;
    }

    // ==================== Global Hover Tilt (disabled) ====================
    // Uncomment to re-enable iOS-like tactile tilt on all planes.
    // Also set postFx.enabled = true above to re-enable lens blur.
    //
    // const TILT_MAX = 0.04; // ~2.3° max tilt
    // const TILT_EASE = 0.12;
    // const TILT_SHRINK = 0.9; // shrink multiplier (~5% at full tilt)
    //
    // let ht = this._hoverTilts.get(plane);
    // if (!ht) {
    //   ht = { tX: 0, tY: 0, cX: 0, cY: 0 };
    //   this._hoverTilts.set(plane, ht);
    // }
    //
    // const b = plane.boundsPx;
    // if (
    //   b && b.w > 0 && b.h > 0 &&
    //   (plane.opacity ?? 1) > 0 &&
    //   this._mousePixelX >= b.x && this._mousePixelX <= b.x + b.w &&
    //   this._mousePixelY >= b.y && this._mousePixelY <= b.y + b.h
    // ) {
    //   const normX = ((this._mousePixelX - b.x) / b.w) * 2 - 1;
    //   const normY = ((this._mousePixelY - b.y) / b.h) * 2 - 1;
    //   ht.tX = -normY * TILT_MAX;
    //   ht.tY = normX * TILT_MAX;
    // } else {
    //   ht.tX = 0;
    //   ht.tY = 0;
    // }
    //
    // const tiltAlpha = 1 - Math.pow(1 - TILT_EASE, fr);
    // ht.cX += (ht.tX - ht.cX) * tiltAlpha;
    // ht.cY += (ht.tY - ht.cY) * tiltAlpha;
    //
    // const hasTilt = Math.abs(ht.cX) > 0.0001 || Math.abs(ht.cY) > 0.0001;
    // const baseRot = plane.rotation ?? [0, 0, 0];
    // if (hasTilt) {
    //   result.rotation = [baseRot[0] + ht.cX, baseRot[1] + ht.cY, baseRot[2]];
    // } else if (plane.rotation !== undefined) {
    //   result.rotation = plane.rotation;
    // }
    //
    // const tiltMag = Math.abs(ht.tX) + Math.abs(ht.tY);
    // if (tiltMag > 0) {
    //   plane.hoverZoom = -tiltMag * TILT_SHRINK;
    // } else if ((plane.hoverZoom ?? 0) < 0) {
    //   plane.hoverZoom = 0;
    // }
    //
    // if (Math.abs(ht.cX - ht.tX) > 0.0001 || Math.abs(ht.cY - ht.tY) > 0.0001) {
    //   this.needsRedraw = true;
    //   this.extraFrames = Math.max(this.extraFrames, 1);
    //   if (this.gpuRenderer) {
    //     this.gpuRenderer.needsRedraw = true;
    //     this.gpuRenderer.extraFrames = Math.max(this.gpuRenderer.extraFrames, 1);
    //   }
    // }

    // Apply hover zoom as bounds scale (negative = shrink toward center)
    if (result.bounds && Math.abs(easedZoom) > 0.0001) {
      const factor = 1 + easedZoom;
      const cx = result.bounds.x + result.bounds.w * 0.5;
      const cy = result.bounds.y + result.bounds.h * 0.5;
      result.bounds.w *= factor;
      result.bounds.h *= factor;
      result.bounds.x = cx - result.bounds.w * 0.5;
      result.bounds.y = cy - result.bounds.h * 0.5;
    }

    // ==================== Nudge (direct repel + tilt, no bounce) ====================
    if (result.bounds) {
      if (!this._oscState.has(plane)) {
        this._oscState.set(plane, { offsetY: 0, tiltX: 0, tiltY: 0 });
      }
      const osc = this._oscState.get(plane)!;

      // Stagger: planes higher on page react slightly more
      const stagger = 1 - Math.min(1, (result.bounds.y + result.bounds.h * 0.5) * NUDGE_STAGGER_SCALE);

      // Scroll-driven targets
      let targetY = this._oscScrollVel * NUDGE_SCROLL_SCALE * stagger;
      targetY = Math.max(-NUDGE_MAX_OFFSET, Math.min(NUDGE_MAX_OFFSET, targetY));
      // Scroll tilts plane along X axis (tips forward/back with scroll direction)
      let targetTiltX = this._oscScrollVel * NUDGE_TILT_SCROLL * stagger * 0.001;
      targetTiltX = Math.max(-NUDGE_TILT_MAX, Math.min(NUDGE_TILT_MAX, targetTiltX));
      let targetTiltY = 0;

      // Mouse repel: push plane away from cursor + tilt away
      const planeCX = result.bounds.x + result.bounds.w * 0.5;
      const planeCY = result.bounds.y + result.bounds.h * 0.5;
      const dx = this._mousePixelX - planeCX;
      const dy = this._mousePixelY - planeCY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < NUDGE_MOUSE_RADIUS && dist > 0) {
        const proximity = 1 - dist / NUDGE_MOUSE_RADIUS;
        const p2 = proximity * proximity;

        // Position repel
        const repel = -dy * p2 * NUDGE_MOUSE_STRENGTH / NUDGE_MOUSE_RADIUS;
        targetY += repel;
        targetY = Math.max(-NUDGE_MAX_OFFSET, Math.min(NUDGE_MAX_OFFSET, targetY));

        // Tilt away from mouse: X-axis tilt from vertical offset, Y-axis tilt from horizontal offset
        const normDx = dx / NUDGE_MOUSE_RADIUS;
        const normDy = dy / NUDGE_MOUSE_RADIUS;
        targetTiltX += normDy * p2 * NUDGE_TILT_MOUSE;
        targetTiltY += -normDx * p2 * NUDGE_TILT_MOUSE;
        targetTiltX = Math.max(-NUDGE_TILT_MAX, Math.min(NUDGE_TILT_MAX, targetTiltX));
        targetTiltY = Math.max(-NUDGE_TILT_MAX, Math.min(NUDGE_TILT_MAX, targetTiltY));
      }

      // Lerp toward targets (smooth, no overshoot)
      const lerpFactor = 1 - Math.pow(1 - NUDGE_LERP_SPEED, fr);
      osc.offsetY += (targetY - osc.offsetY) * lerpFactor;
      osc.tiltX += (targetTiltX - osc.tiltX) * lerpFactor;
      osc.tiltY += (targetTiltY - osc.tiltY) * lerpFactor;

      // Clamp and snap to zero when close
      osc.offsetY = Math.max(-NUDGE_MAX_OFFSET, Math.min(NUDGE_MAX_OFFSET, osc.offsetY));
      if (Math.abs(osc.offsetY) < 0.01 && Math.abs(targetY) < 0.01) osc.offsetY = 0;
      if (Math.abs(osc.tiltX) < 0.0001 && Math.abs(targetTiltX) < 0.0001) osc.tiltX = 0;
      if (Math.abs(osc.tiltY) < 0.0001 && Math.abs(targetTiltY) < 0.0001) osc.tiltY = 0;

      // Apply offset
      result.bounds.y += osc.offsetY;

      // Apply tilt rotation
      if (Math.abs(osc.tiltX) > 0.0001 || Math.abs(osc.tiltY) > 0.0001) {
        const baseRot = result.rotation ?? plane.rotation ?? [0, 0, 0];
        result.rotation = [baseRot[0] + osc.tiltX, baseRot[1] + osc.tiltY, baseRot[2]];
      }

      // Keep rendering while settling
      const active = Math.abs(osc.offsetY) > 0.01 || Math.abs(targetY) > 0.01
        || Math.abs(osc.tiltX) > 0.0001 || Math.abs(osc.tiltY) > 0.0001;
      if (active) {
        this.needsRedraw = true;
        this.extraFrames = Math.max(this.extraFrames, 1);
        if (this.gpuRenderer) {
          this.gpuRenderer.needsRedraw = true;
          this.gpuRenderer.extraFrames = Math.max(this.gpuRenderer.extraFrames, 1);
        }
      }
    }

    return result;
  }

  /**
   * Renders a single scene.
   */
  private _renderScene(scene: Scene | null): void {
    if (!this.gpuRenderer || !scene) return;

    const gpuPlanes: GPUPlane[] = scene.planes.map((p) =>
      this._convertPlane(p, 1.0)
    );

    const gpuScene: GPUScene = {
      planes: gpuPlanes,
    };

    // Use post-processing if enabled
    if (this.postFx.enabled) {
      this._renderWithPostFx(gpuScene);
    } else {
      this.gpuRenderer.render(gpuScene);
    }
  }

  /**
   * Renders two scenes with crossfade transition.
   */
  private _renderPair(
    fromScene: Scene,
    toScene: Scene,
    progress: number
  ): void {
    if (!this.gpuRenderer) return;

    const p = Math.min(1, Math.max(0, progress || 0));

    const fromPlanes = fromScene.planes.map((plane) =>
      this._convertPlane(plane, 1.0 - p)
    );
    const toPlanes = toScene.planes.map((plane) =>
      this._convertPlane(plane, p)
    );

    const gpuScene: GPUScene = {
      planes: [...fromPlanes, ...toPlanes],
    };

    // Use post-processing if enabled
    if (this.postFx.enabled) {
      this._renderWithPostFx(gpuScene);
    } else {
      this.gpuRenderer.render(gpuScene);
    }
  }

  /**
   * Renders scene with post-processing effects.
   */
  private _renderWithPostFx(gpuScene: GPUScene): void {
    if (!this.gpuRenderer || !this.device) {
      return;
    }

    // Initialize post-fx on first use
    if (!this._postFxReady) {
      this._initPostFx();
    }

    // Fall back to direct rendering if post-fx not ready
    if (!this._postProcessor || !this._sceneTextureView) {
      this.gpuRenderer.render(gpuScene);
      return;
    }

    const context = this.gpuRenderer.getContext();
    if (!context) {
      this.gpuRenderer.render(gpuScene);
      return;
    }

    // Update time using actual delta
    const now = performance.now();
    const dt = this._prevFrameTime
      ? (now - this._prevFrameTime) / 1000
      : 1 / 60;
    this._prevFrameTime = now;
    this._time += dt;

    const { width, height } = this.gpuRenderer.getCanvasSize();
    if (width === 0 || height === 0) return;

    // Update distortion uniforms (if enabled)
    if (this._distortPass?.enabled) {
      const distUniforms = new Float32Array([
        width, height,
        this._time,
        this.postFx.amount,
        this.postFx.frequency,
        this.postFx.speed,
        this._mouseX,
        this._mouseY,
        3,
        this.postFx.radius,
        0, 0,
      ]);
      this._distortPass.updateUniforms(this.device, distUniforms.buffer);
    }

    // Update lens blur uniforms (if enabled)
    if (this._lensPass?.enabled) {
      const lensUniforms = new Float32Array([
        width, height,   // resolution
        this._time,      // time
        0,               // _pad
        this._mouseX,    // mouse.x
        this._mouseY,    // mouse.y
        0, 0,            // _pad2
      ]);
      this._lensPass.updateUniforms(this.device, lensUniforms.buffer);
    }

    // Create command encoder
    const encoder = this.device.createCommandEncoder();

    // Render scene to offscreen texture
    this.gpuRenderer.renderToTexture(gpuScene, encoder, this._sceneTextureView);

    // Get canvas texture
    const canvasTexture = context.getCurrentTexture();
    const canvasView = canvasTexture.createView();

    // Apply post-processing
    this._postProcessor.render(encoder, this._sceneTextureView, canvasView);

    // Submit
    this.device.queue.submit([encoder.finish()]);
  }

  /**
   * Resizes the renderer.
   */
  resize(): void {
    this.gpuRenderer?.resize();

    // Resize post-processing if initialized
    if (this._postFxReady) {
      this._resizePostFx();
    }
  }

  /**
   * Register a callback that runs at the top of each frame,
   * before bounds are read and the render pass executes.
   */
  setPreRender(cb: (() => void) | null): void {
    this._preRenderCb = cb;
  }

  /**
   * Toggle video playback across all scenes.
   * Set to true to play, false to pause.
   */
  setVideosPlaying(playing: boolean): void {
    this.videosPlaying = playing;
    for (const key of Object.keys(this.scenes)) {
      const scene = this.scenes[key];
      if (!scene) continue;
      for (const plane of scene.planes) {
        if (!plane.videoTexture) continue;
        if (playing) {
          plane.videoTexture.play();
        } else {
          plane.videoTexture.pause();
        }
      }
    }
    if (playing) {
      this.requestRedraw();
    }
  }

  /**
   * Freeze or unfreeze DOM bounds measurements.
   * When frozen, plane positions stop syncing with the DOM.
   */
  freezeBounds(frozen: boolean): void {
    this.boundsFrozen = frozen;
    if (!frozen) {
      this.requestBoundsUpdate();
      this.requestExtraFrames();
    }
  }

  /**
   * Requests bounds update on next frame.
   */
  requestBoundsUpdate(): void {
    this.boundsUpdate.force = true;
    this.needsRedraw = true;
    this.extraFrames = Math.max(this.extraFrames, 2);
    if (this.renderer) {
      this.renderer.needsRedraw = true;
      this.renderer.extraFrames = Math.max(this.renderer.extraFrames, 2);
    }
    if (this.gpuRenderer) {
      this.gpuRenderer.needsRedraw = true;
      this.gpuRenderer.extraFrames = Math.max(this.gpuRenderer.extraFrames, 2);
    }
  }

  /**
   * Requests a redraw on next frame.
   */
  requestRedraw(): void {
    this.needsRedraw = true;
    this.extraFrames = Math.max(this.extraFrames, 2);
    if (this.renderer) {
      this.renderer.needsRedraw = true;
      this.renderer.extraFrames = Math.max(this.renderer.extraFrames, 2);
    }
    if (this.gpuRenderer) {
      this.gpuRenderer.needsRedraw = true;
      this.gpuRenderer.extraFrames = Math.max(this.gpuRenderer.extraFrames, 2);
    }
  }

  /**
   * Sets bounds update mode.
   */
  setBoundsUpdateMode(mode: "always" | "throttled"): void {
    this.boundsUpdate.mode = mode;
  }

  /**
   * Prepares a crossfade transition between routes.
   */
  prepareTransition(fromUrl: string, toUrl: string, _mode?: string): void {
    if (!fromUrl || !toUrl || fromUrl === toUrl) return;

    const fromKey = this.resolveRouteKey(fromUrl) ?? fromUrl;
    const toKey = this.resolveRouteKey(toUrl) ?? toUrl;

    if (!this.scenes[fromKey]) {
      this.scenes[fromKey] = new Scene(fromKey, this, App.route.old.page);
    }
    if (!this.scenes[toKey]) {
      this.scenes[toKey] = new Scene(toKey, this, App.route.new.page);
    }

    this.isMutating = true;
    this.transition = {
      fromScene: this.scenes[fromKey] ?? null,
      toScene: this.scenes[toKey] ?? null,
      progress: 0,
    };

    this.requestExtraFrames();
  }

  /**
   * Finalizes a route transition.
   */
  commitTransition(): void {
    if (this.transition?.toScene) {
      this.scene = this.transition.toScene;
    }

    this.isMutating = false;
    this.transition = null;
    this.requestExtraFrames();
  }

  /**
   * Destroys the GPU orchestrator and cleans up resources.
   */
  destroy(): void {
    if (this._raf) {
      this._raf.stop();
      this._raf = null;
    }

    if (this._roId !== null) {
      ResizeHub.remove(this._roId);
      this._roId = null;
    }

    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      this._onScroll = null;
    }

    if (this._oscWheelListener) {
      document.removeEventListener("wheel", this._oscWheelListener, { capture: true } as EventListenerOptions);
      this._oscWheelListener = null;
    }

    // Clean up post-processing
    this._postProcessor?.destroy();
    this._postProcessor = null;
    this._distortPass = null;
    this._lensPass = null;
    this._sceneTexture?.destroy();
    this._sceneTexture = null;
    this._sceneTextureView = null;
    this._postFxReady = false;

    this.gpuRenderer?.destroy();
    this.gpuRenderer = null;
    this.device = null;
  }
}
